import { zodResolver } from '@hookform/resolvers/zod'
import { Button, FormField, PasswordInput, Skeleton, toast } from '@gestao/ui'
import { useQuery } from '@tanstack/react-query'
import { LinkIcon } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router'

import { usePageTitle } from '../../app/use-page-title'
import { FormAlert } from '../../components/form-alert'
import { authLinkClasses } from './auth-link'
import * as t from '../../i18n/pt-BR'
import { errorMessage, fieldErrors, isApiError } from '../../lib/api'
import { canAutoFocus } from '../../lib/auto-focus'
import { authApi, forgetLocalSession, RESET_TOKEN_PATTERN } from './api'
import { PasswordRules } from './password-rules'
import { type ResetPasswordForm, ResetPasswordFormSchema } from './password-schemas'

/**
 * /redefinir-senha/:token (link do e-mail, 30 min, uso único). Ao abrir, confere o link (sem
 * consumi-lo) e, se não vale, mostra o aviso na hora. Sucesso encerra todas as sessões,
 * inclusive a local, e volta ao login com um aviso.
 */
export function ResetPasswordPage() {
  usePageTitle(t.auth.reset.title)
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const rulesId = useId()
  const [rejected, setRejected] = useState(!RESET_TOKEN_PATTERN.test(token))
  const status = useQuery({
    queryKey: ['reset-password-token', token],
    queryFn: ({ signal }) => authApi.resetTokenStatus(token, signal),
    enabled: !rejected,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  })
  // Só o 404 do link reprova na abertura; outra falha (rede) deixa o formulário, e o envio decide.
  const invalidLink = rejected || (status.isError && isApiError(status.error, 'AUTH_TOKEN_INVALID'))
  const checking = !invalidLink && status.isPending
  const [formError, setFormError] = useState<string | null>(null)
  const invalidHeadingRef = useRef<HTMLHeadingElement>(null)
  const {
    control,
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(ResetPasswordFormSchema),
    defaultValues: { password: '', confirmPassword: '' },
  })
  const password = useWatch({ control, name: 'password' })

  useEffect(() => {
    if (invalidLink) invalidHeadingRef.current?.focus()
    else if (!checking && canAutoFocus()) setFocus('password')
  }, [invalidLink, checking, setFocus])

  const onSubmit = handleSubmit(async ({ password: next }) => {
    setFormError(null)
    try {
      await authApi.resetPassword({ token, password: next })
      forgetLocalSession()
      toast.success(t.auth.reset.success)
      await navigate('/login', { replace: true })
    } catch (error) {
      if (isApiError(error, 'AUTH_TOKEN_INVALID')) {
        setRejected(true)
        return
      }
      const message = fieldErrors(error).password
      if (message) setError('password', { message }, { shouldFocus: true })
      else if (fieldErrors(error).token) setRejected(true)
      else setFormError(errorMessage(error))
    }
  })

  if (invalidLink) {
    return (
      <div className="flex flex-col gap-6">
        <div
          aria-hidden
          className="grid size-12 place-items-center rounded-lg bg-framboesa/10 text-framboesa [&_svg]:size-6"
        >
          <LinkIcon />
        </div>
        <div className="flex flex-col gap-2">
          <h1 ref={invalidHeadingRef} tabIndex={-1} className="text-page text-ink outline-none">
            {t.auth.reset.invalidTitle}
          </h1>
          <p className="text-body text-ink-2">{t.auth.reset.invalidDescription}</p>
        </div>
        <div className="flex flex-col gap-4">
          <Button asChild className="h-10 w-full">
            <Link to="/esqueci-senha">{t.auth.reset.requestNew}</Link>
          </Button>
          <Link to="/login" className={authLinkClasses}>
            {t.auth.forgot.back}
          </Link>
        </div>
      </div>
    )
  }

  if (checking) {
    return (
      <div className="flex flex-col gap-6" aria-busy>
        <div className="flex flex-col gap-2">
          <h1 className="text-page text-ink">{t.auth.reset.title}</h1>
          <p className="text-body text-ink-2">{t.auth.reset.checking}</p>
        </div>
        <div aria-hidden className="flex flex-col gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    )
  }

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-page text-ink">{t.auth.reset.title}</h1>
        <p className="text-body text-ink-2">{t.auth.reset.description}</p>
      </div>

      <div className="flex flex-col gap-4">
        <FormField label={t.auth.reset.password} error={errors.password?.message}>
          <PasswordInput
            autoComplete="new-password"
            toggleLabel={t.auth.showPassword}
            aria-describedby={rulesId}
            className="h-10"
            {...register('password')}
          />
        </FormField>
        <PasswordRules id={rulesId} password={password} />
        <FormField label={t.auth.reset.confirm} error={errors.confirmPassword?.message}>
          <PasswordInput
            autoComplete="new-password"
            toggleLabel={t.auth.showPassword}
            className="h-10"
            {...register('confirmPassword')}
          />
        </FormField>
      </div>

      {formError ? <FormAlert>{formError}</FormAlert> : null}

      <div className="flex flex-col gap-4">
        <Button type="submit" loading={isSubmitting} className="h-10 w-full">
          {t.auth.reset.submit}
        </Button>
        <Link to="/login" className={authLinkClasses}>
          {t.auth.forgot.back}
        </Link>
      </div>
    </form>
  )
}
